import { type ReactNode, useState } from 'react';

import { CheckIcon, SettingsIcon, XIcon } from 'lucide-react';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { MultiSelect } from 'primereact/multiselect';
import { SplitButton } from 'primereact/splitbutton';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useDeactivateDonorSchedulesMutation } from '@/app/services/donorSchedules';
import { DonorSchedulingActions } from '@/app/slices/donorScheduling';
import { PartnershipsActions } from '@/app/slices/partnerships';
import { addToast } from '@/app/slices/toast';
import { CommentDialog } from '@/components';
import { getRevisionCommentDialogConfig } from '@/components/common/revisions-button/revision-comment-dialog-config';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { Button } from '@/components/ui/button';
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import {
  Dialog as BaseDialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useReactivateDonorSchedules } from '@/services/donor-scheduling';
import { useMassSetField } from '@/services/donor-scheduling/hooks';
import { DonorSchedule } from '@/types.gql';

import { CLEARABLE_FIELDS } from '../../constants';
import { ClearableField } from '../../types';
import {
  EditSoScheduleDialog,
  getEditableSoScheduleId,
} from '../edit-so-schedule-dialog';
import { ToolbarMenuButton } from './ToolbarMenuButton';

type ActionsSplitButtonProps = {
  showAddRevisionCommentsDialog: boolean;
  setShowAddRevisionCommentsDialog: (show: boolean) => void;
  handleGetValue: (fieldName: string) => string | null;
  handleSetValue: (fieldName: string, value: string) => void;
  setMissingFieldsDialogVisible: (visible: boolean) => void;
  variant?: 'prime' | 'base-ui';
};

type ConfirmState = {
  title: string;
  body: ReactNode;
  onConfirm: () => void;
};

export const ActionsSplitButton = ({
  showAddRevisionCommentsDialog,
  setShowAddRevisionCommentsDialog,
  handleGetValue,
  handleSetValue,
  setMissingFieldsDialogVisible,
  variant = 'prime',
}: ActionsSplitButtonProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [currentFields, setCurrentFields] = useState<ClearableField[] | null>(
    [],
  );
  const [clearDialogVisible, setClearDialogVisible] = useState(false);
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
  const [editSoScheduleVisible, setEditSoScheduleVisible] = useState(false);
  const isBaseUi = variant === 'base-ui';

  const { selectedDonorSchedules } = useAppSelector(
    (state) => state.donorScheduling,
  );

  const [deactivateDonorSchedule] = useDeactivateDonorSchedulesMutation();
  const reactivateDonorSchedules = useReactivateDonorSchedules();
  const { mutateAsync: massSetField } = useMassSetField();

  const isMultiSelectEnabled = useFeatureFlag(
    'Select_Multiple_Chute_Schedules',
  );
  const revisionCommentDialogConfig =
    getRevisionCommentDialogConfig(isMultiSelectEnabled);

  const isReactivateSchedulesEnabled = useFeatureFlag(
    'DS_Reactivate_Schedules',
  );
  const isClearAspirationCommentsEnabled = useFeatureFlag(
    'DS_clear_aspiration_comments',
  );
  const isTransferFieldClearEnabled = useFeatureFlag('DS_Transfer_Field_Clear');
  const isUpdateSoSchedulesEnabled = useFeatureFlag('Update_So_Schedules');
  const clearableFields = CLEARABLE_FIELDS.filter(
    (field) =>
      (isClearAspirationCommentsEnabled ||
        field.value !== 'specialInstructions') &&
      (isTransferFieldClearEnabled || field.value !== 'transfer'),
  );

  const selectedCount = selectedDonorSchedules?.length ?? 0;
  const editableSoScheduleId = getEditableSoScheduleId(selectedDonorSchedules);

  const handleDeactivate = async () => {
    try {
      const donorScheduleIds = selectedDonorSchedules?.map(
        (schedule: DonorSchedule) => schedule.id,
      );
      await deactivateDonorSchedule({ donorScheduleIds })
        .unwrap()
        .then(() => {
          dispatch({
            type: DonorSchedulingActions.INCREMENT_REFETCH_TRIGGER,
          });
          dispatch({
            type: DonorSchedulingActions.RESET_SELECTED_DONOR_SCHEDULING,
          });
          dispatch({
            type: DonorSchedulingActions.RESET_WORKING_DONOR_SCHEDULE,
          });
          dispatch({
            type: PartnershipsActions.RESET_PARTNERSHIP,
          });
        });
    } catch (error) {
      console.error('Failed to Deactivate donor schedules:', error);
    }
  };

  const openConfirm = ({
    message,
    header,
    accept,
  }: {
    message: ReactNode;
    header: string;
    accept: () => void;
  }) => {
    if (isBaseUi) {
      setConfirmState({ title: header, body: message, onConfirm: accept });
      return;
    }
    confirmDialog({
      message,
      header,
      icon: 'pi pi-exclamation-triangle',
      accept,
      acceptLabel: t('common.yes'),
      rejectLabel: t('common.no'),
    });
  };

  const handleDeactivateClick = () => {
    const scheduleNames = selectedDonorSchedules
      .map((schedule: DonorSchedule) => schedule.name)
      .join(', ');

    const options = {
      message: isReactivateSchedulesEnabled ? (
        <div className="flex flex-col gap-3">
          <p className="m-0">
            {t('donorScheduling.deactivateConfirmCount', {
              count: selectedCount,
            })}
          </p>
          <ul className="m-0 max-h-60 list-disc overflow-y-auto pl-6">
            {selectedDonorSchedules?.map((schedule: DonorSchedule) => (
              <li key={schedule.id}>{schedule.name}</li>
            ))}
          </ul>
          <p className="m-0">{t('donorScheduling.confirmContinue')}</p>
        </div>
      ) : (
        t('donorScheduling.deactivateConfirmMessage', {
          names: scheduleNames,
        })
      ),
      header: t('donorScheduling.deactivateDonorSchedules'),
      accept: handleDeactivate,
    };
    openConfirm(options);
  };

  // Inactive schedules only: an all-active selection is a no-op, mirroring
  // how Deactivate behaves on already-inactive schedules.
  const inactiveSelectedSchedules = (selectedDonorSchedules ?? []).filter(
    (schedule: DonorSchedule) => schedule.stateCode === 1,
  );

  const handleReactivate = async () => {
    if (inactiveSelectedSchedules.length === 0) return;

    try {
      const donorScheduleIds = inactiveSelectedSchedules.map(
        (schedule: DonorSchedule) => schedule.id,
      );
      await reactivateDonorSchedules.mutateAsync(donorScheduleIds);

      dispatch({
        type: DonorSchedulingActions.INCREMENT_REFETCH_TRIGGER,
      });
      dispatch({
        type: DonorSchedulingActions.RESET_SELECTED_DONOR_SCHEDULING,
      });
      dispatch({
        type: DonorSchedulingActions.RESET_WORKING_DONOR_SCHEDULE,
      });
      dispatch({
        type: PartnershipsActions.RESET_PARTNERSHIP,
      });
    } catch (error) {
      console.error('Failed to Reactivate donor schedules:', error);
    }
  };

  const handleReactivateClick = () => {
    if (inactiveSelectedSchedules.length === 0) return;

    openConfirm({
      message: (
        <div className="flex flex-col gap-3">
          <p className="m-0">
            {t('donorScheduling.reactivateConfirmCount', {
              count: inactiveSelectedSchedules.length,
            })}
          </p>
          <ul className="m-0 max-h-60 list-disc overflow-y-auto pl-6">
            {inactiveSelectedSchedules.map((schedule: DonorSchedule) => (
              <li key={schedule.id}>{schedule.name}</li>
            ))}
          </ul>
          <p className="m-0">{t('donorScheduling.confirmContinue')}</p>
        </div>
      ),
      header: t('donorScheduling.reactivateDonorSchedules'),
      accept: handleReactivate,
    });
  };

  const handleClear = async () => {
    if (currentFields?.length === 0) return;

    try {
      const donorScheduleIds = selectedDonorSchedules?.map(
        (schedule: DonorSchedule) => schedule.id,
      );

      let fieldsToClear = currentFields ? [...currentFields] : [];

      if (currentFields?.includes('allSemenRelatedFields')) {
        fieldsToClear = fieldsToClear.filter(
          (field) => field !== 'allSemenRelatedFields',
        );
        fieldsToClear.push(
          'semenId',
          'cane',
          'colCode',
          'naab',
          'sireId',
          'semenType',
          'semenLocationId',
          'semenOwnerId',
          'semenComment',
        );
      }

      await massSetField({
        donorScheduleIds,
        fields: fieldsToClear,
        value: '',
      });
      dispatch({
        type: DonorSchedulingActions.INCREMENT_REFETCH_TRIGGER,
      });
      dispatch({
        type: DonorSchedulingActions.RESET_WORKING_DONOR_SCHEDULE,
      });
      dispatch({
        type: PartnershipsActions.RESET_PARTNERSHIP,
      });
      dispatch(
        addToast({
          severity: 'success',
          summary: t('clearField.fieldsSuccessfullyCleared'),
          detail: t('clearField.fieldsSuccessfullyClearedDetail', {
            count: selectedCount,
          }),
          life: ToastLife.DEFAULT,
        }),
      );
      setClearDialogVisible(false);
    } catch (error) {
      console.error('Failed to mass clear field:', error);
      dispatch(
        addToast({
          severity: 'error',
          summary: t('clearField.failedToClearFields'),
          detail: `${error}`,
          life: ToastLife.DEFAULT,
        }),
      );
    }
  };

  const clearDialogFooter = (
    <div className="flex justify-end gap-2">
      <button
        type="button"
        onClick={() => setClearDialogVisible(false)}
        className="flex items-center gap-2 rounded-md border-none bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200 focus:ring-2 focus:ring-gray-300"
      >
        <i className="pi pi-times" />
        {t('common.cancel')}
      </button>
      <button
        type="button"
        onClick={handleClear}
        disabled={currentFields?.length === 0}
        className="flex items-center gap-2 rounded-md border-none bg-blue-400 px-4 py-2 text-white hover:bg-blue-500 focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <i className="pi pi-check" />
        {t('common.clear')}
      </button>
    </div>
  );

  const actionItems = [
    {
      label: t('common.deactivate'),
      icon: 'pi pi-trash',
      command: handleDeactivateClick,
    },
    ...(isReactivateSchedulesEnabled
      ? [
          {
            label: t('common.reactivate'),
            icon: 'pi pi-replay',
            command: handleReactivateClick,
          },
        ]
      : []),
    {
      label: t('clearField.clearField'),
      icon: 'pi pi-eraser',
      command: () => setClearDialogVisible(true),
    },
    {
      label: t('common.missingFields'),
      icon: 'pi pi-question',
      command: () => setMissingFieldsDialogVisible(true),
    },
    ...(isUpdateSoSchedulesEnabled
      ? [
          {
            label: t('donorScheduling.editSoSchedule.title'),
            icon: 'pi pi-pencil',
            disabled: !editableSoScheduleId,
            command: () => setEditSoScheduleVisible(true),
          },
        ]
      : []),
    {
      label: t('revision.revisionComments'),
      icon: 'pi pi-plus',
      command: () => {
        if (
          selectedCount >= 1 &&
          (selectedCount === 1 || isMultiSelectEnabled)
        ) {
          setShowAddRevisionCommentsDialog(true);
        } else {
          dispatch(
            addToast({
              severity: 'error',
              summary: t('revision.revisionComments'),
              detail: t('donorScheduling.selectOneDonorSchedule'),
              life: ToastLife.DEFAULT,
            }),
          );
        }
      },
    },
  ];

  return (
    <>
      {isBaseUi ? (
        <ToolbarMenuButton
          label={t('common.actions')}
          icon={<SettingsIcon />}
          model={actionItems}
          disabled={selectedCount === 0}
        />
      ) : (
        <SplitButton
          label={t('common.actions')}
          icon="pi pi-cog"
          model={actionItems}
          severity="secondary"
          disabled={selectedCount === 0}
        />
      )}

      {isBaseUi ? (
        <>
          <BaseDialog
            open={!!confirmState}
            onOpenChange={(open) => !open && setConfirmState(null)}
          >
            <DialogContent className="md:max-w-md">
              <DialogHeader>
                <DialogTitle>{confirmState?.title}</DialogTitle>
              </DialogHeader>
              <DialogBody>{confirmState?.body}</DialogBody>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfirmState(null)}>
                  {t('common.no')}
                </Button>
                <Button
                  onClick={() => {
                    confirmState?.onConfirm();
                    setConfirmState(null);
                  }}
                >
                  {t('common.yes')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </BaseDialog>

          <BaseDialog
            open={clearDialogVisible}
            onOpenChange={setClearDialogVisible}
          >
            <DialogContent className="md:max-w-lg">
              <DialogHeader>
                <DialogTitle>{t('clearField.clearField')}</DialogTitle>
              </DialogHeader>
              <DialogBody className="flex flex-col gap-3">
                <p>{t('clearField.selectFieldsToClearForSchedules')}</p>
                <ul className="list-disc pl-6">
                  {selectedDonorSchedules?.map((schedule: DonorSchedule) => (
                    <li key={schedule.id}>{schedule.name}</li>
                  ))}
                </ul>
                <Combobox
                  items={clearableFields}
                  multiple
                  openOnInputClick
                  value={clearableFields.filter((field) =>
                    currentFields?.includes(field.value as ClearableField),
                  )}
                  onValueChange={(next: typeof clearableFields) =>
                    setCurrentFields(
                      next.map((field) => field.value as ClearableField),
                    )
                  }
                  itemToStringValue={(field) => field.value}
                  itemToStringLabel={(field) => field.label}
                >
                  <ComboboxChips className="w-full">
                    {clearableFields
                      .filter((field) =>
                        currentFields?.includes(field.value as ClearableField),
                      )
                      .map((field) => (
                        <ComboboxChip
                          key={field.value}
                          aria-label={field.label}
                        >
                          {field.label}
                        </ComboboxChip>
                      ))}
                    <ComboboxChipsInput
                      aria-label={t('clearField.selectFieldToClear')}
                      placeholder={
                        currentFields?.length
                          ? ''
                          : t('clearField.selectFieldToClear')
                      }
                    />
                  </ComboboxChips>
                  <ComboboxContent>
                    <ComboboxEmpty>{t('common.noItemsFound')}</ComboboxEmpty>
                    <ComboboxList>
                      {(field: (typeof clearableFields)[number]) => (
                        <ComboboxItem key={field.value} value={field}>
                          {field.label}
                        </ComboboxItem>
                      )}
                    </ComboboxList>
                  </ComboboxContent>
                </Combobox>
              </DialogBody>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setClearDialogVisible(false)}
                >
                  <XIcon />
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleClear} disabled={!currentFields?.length}>
                  <CheckIcon />
                  {t('common.clear')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </BaseDialog>
        </>
      ) : (
        <Dialog
          visible={clearDialogVisible}
          onHide={() => setClearDialogVisible(false)}
          header={t('clearField.clearField')}
          footer={clearDialogFooter}
          className="min-w-[450px]"
        >
          <div className="flex flex-col gap-4">
            <p className="m-0 leading-relaxed">
              {t('clearField.selectFieldsToClearForSchedules')}
            </p>
            <ul className="list-disc pl-6">
              {selectedDonorSchedules?.map((schedule: DonorSchedule) => (
                <li key={schedule.id}>{schedule.name}</li>
              ))}
            </ul>

            <div className="flex w-full gap-2">
              <MultiSelect
                value={currentFields}
                options={clearableFields}
                optionLabel="label"
                optionValue="value"
                display="chip"
                maxSelectedLabels={3}
                onChange={(e) => setCurrentFields(e.value as ClearableField[])}
                placeholder={t('clearField.selectFieldToClear')}
                filter={true}
                showSelectAll={false}
                className="flex-1"
              />
            </div>
          </div>
        </Dialog>
      )}

      {isUpdateSoSchedulesEnabled && (
        <EditSoScheduleDialog
          visible={editSoScheduleVisible}
          soScheduleId={editableSoScheduleId}
          onHide={() => setEditSoScheduleVisible(false)}
          onSaved={() =>
            dispatch({ type: DonorSchedulingActions.INCREMENT_REFETCH_TRIGGER })
          }
        />
      )}

      {/* Stays PrimeReact: CommentDialog's PrimeReact overlays don't work inside a Base UI modal. */}
      <Dialog
        visible={showAddRevisionCommentsDialog}
        onHide={() => setShowAddRevisionCommentsDialog(false)}
        className="w-[80vw]"
      >
        <CommentDialog
          config={revisionCommentDialogConfig}
          setValue={handleSetValue}
          getValue={handleGetValue}
          onClose={() => setShowAddRevisionCommentsDialog(false)}
        />
      </Dialog>
    </>
  );
};
